<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-item-list">
        <i class="far fa-list"></i> NCR Item List
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-item-list">
        <DataTable
          ref="dataTable"
          :columns="columnList"
          defaultColumnOrderDir="DESC"
          :defaultColumnOrderIndex="0"
          :url="dataURL"
          :filterOptions="panelFilterOptions"
          :displayTools="true"
          :hasDateRangeFilter="true"
          :displayLocationFilter="true"
          displayLocationFilterField="orderAccountLocationId"
          :displayExportTools="true"
          :useReportingServer="true"
          exportTitle="NCR Items"
          :debounceTime="300"
        >
          <template v-slot:title>
            <slot name="title" />
          </template>
          <template v-slot:tools>
            <slot name="tools" />
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                <LinkOrder :id="row.data.orderId" :name="row.data.orderSequenceNumber" />
              </td>
              <td>
                <LinkContact :id="row.data.orderContactId" :name="row.data.orderContactName" />
              </td>
              <td>
                <LabelAccountLocationBadge :accountLocationId="row.data.orderAccountLocationId" />
              </td>
              <td>
                <Tooltip :label="row.data.stockName"><LinkStock :id="row.data.stockId" :name="row.data.stockCode" /></Tooltip>
              </td>
              <td>{{row.data.glassHeight}} x {{row.data.glassWidth}}</td>
              <td>
                <LabelTags :labelList="row.data.labelList" />
              </td>
              <td>
                {{row.data.ncrRequestReason}}
              </td>
              <td>
                {{formatDateTime(row.data.dateOrderCreated)}}
              </td>
              <td>
                {{formatDateTime(row.data.dateClosed)}}
              </td>
              <td>
                <LabelBadge :labelClass="`label-approval-${row.data.orderApprovalStatus}`">{{formatLookupName(row.data.orderApprovalStatus, ApprovalTypeLookup)}}</LabelBadge>
              </td>
              <td>
                <span v-if="row.data.orderApprovalStatus == ApprovalTypes.approved">
                  {{row.data.orderApprovalApprovedNote}}
                </span>
                <span v-if="row.data.orderApprovalStatus == ApprovalTypes.denied">
                  {{row.data.orderApprovalDeniedReason}}
                </span>
              </td>
              <td>
                {{row.data.quantity}}
              </td>
              <td>
                {{row.data.orderApprovalAcceptedQuantity}}
              </td>
              <td>
                {{row.data.orderApprovalRejectedQuantity}}
              </td>
              <td>
                <span v-if="row.data.orderApprovalAcceptedQuantity > 0">
                  <i class="far fa-check-circle status-approved mr-2"></i> {{row.data.orderApprovalAcceptedQuantity}} Accepted
                </span>
                <span v-if="row.data.orderApprovalRejectedQuantity > 0">
                  <i class="far fa-times-circle status-denied mr-2"></i> {{row.data.orderApprovalRejectedQuantity}} Rejected
                </span>
              </td>
              <td>
                {{formatCurrency(row.data.acceptedPanelProcessingValue)}}
              </td>              
            </tr>
          </template>
          <template v-slot:summary="summary">
            <tr key="summary">
              <td>Totals</td>
              <td colspan="10"></td>
              <td>{{summary.data.totalQuantity}}</td>
              <td>{{summary.data.totalAcceptedQuantity}}</td>
              <td>{{summary.data.totalRejectedQuantity}}</td>
              <td></td>
              <td>{{formatCurrency(summary.data.totalAcceptedPanelProcessingValue)}}</td>
            </tr>
          </template>
        </DataTable>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'

import moment from 'moment'
import buildUrl from 'build-url'
import { mapState } from 'vuex'

import Utils from '@/utils/utils'
import { ApprovalTypes, ApprovalTypeLookup } from '@/store/types'

export default {
  name: 'NCRValue',
  components: {
  },
  data() {
    return {
      columnList: [
        { title: "Order", data: "orderSequenceNumber" },
        { title: "Customer", data: "orderContactName" },
        { title: "Location", data: "orderAccountLocationName" },
        { title: "Stock", data: "stockName" },
        { title: "Size", data: "size" },
        { title: "Labels", data: "labelListNames", type: 'label' },
        { title: "Reason", data: 'ncrRequestReason' },
        { title: "NCR Created", data: "dateOrderCreated", dateFilter: true, type: 'datetime' },
        { title: "NCR Completed", data: "dateClosed", dateFilter: true, type: 'datetime' },
        { title: "Approval", data: "orderApprovalStatus" },
        { title: "Notes", data: "", sortable: false },
        { title: "Quantity", data: "quantity", sortable: true },
        { title: "Accepted", data: "orderApprovalAcceptedQuantity", sortable: true },
        { title: "Rejected", data: "orderApprovalRejectedQuantity", sortable: true },
        { title: "Acceptance", data: "acceptance", sortable: false },
        { title: "NCR Cost", data: "acceptedPanelProcessingValue", type: 'currency' },
      ]
    }
  },
  computed: {
    ...mapState({
      taxName: state => state.account.salesTaxRate.name
    }),
    dataURL() {
      var params = {
        filter: {},
        includesummary: true
      }      

      return '/intelligence/orders/ncrvalue/orderitems?' + $.param(params)
    },
    panelFilterOptions() {
      var filterOptions = [
      ]

      let ncrLabels = this.$store.getters['label/getLabelsByType']('orderItemNCR')
      filterOptions.push({
        name: 'NCR Label',
        field: 'labelListNames',
        operator: 'like',
        options: ncrLabels.reverse().map(labelInfo => {
          return {
            name: labelInfo.name,
            value: '%' + labelInfo.name + '%',
            colour: labelInfo.colour
          }
        })
      })

      filterOptions.push({
        name: 'Approval',
        field: 'orderApprovalStatus',
        columnIndex: 1,
        options: [
          { name: Utils.getLookupName(ApprovalTypeLookup, ApprovalTypes.approved), value: ApprovalTypes.approved, class: 'label-approval-approved' },
          { name: Utils.getLookupName(ApprovalTypeLookup, ApprovalTypes.denied), value: ApprovalTypes.denied, class: 'label-approval-denied' },
        ]
      })

      return filterOptions
    }
  },
  created() {
    this.ApprovalTypes = ApprovalTypes
    this.ApprovalTypeLookup = ApprovalTypeLookup
  },
  mounted() {
    this.requestFilterLabels()
  },
  beforeDestroy() {
  },
  methods: {
    requestFilterLabels() {
      return new Promise(async (resolve, reject) => {
        try {
          // Get the label info for the filter popovers
          let ncrLabels = await this.$apiService.coreService.getLabelsForType('orderItemNCR')
          this.$store.commit({
            type: 'label/updateLabels',
            labelType: 'orderItemNCR',
            labelList: ncrLabels.data
          })

          return resolve()
        } catch(err) {
          return reject(err)
        }
      })      
    }
  }
}
</script>
